import { bindAssetMentionTokens, buildAssetMentionBindingTable } from "./asset-mentions";
import { assetRefKeysIn } from "./asset-ref-tokens";
import { priceVersionsOf } from "./price-versions";

/**
 * Which tested assets a piece of respondent text names: by its `@`-mention
 * tokens (`assetRefToken`, by key), a typed `@Name` read as the one binder
 * binds it. Literal references only, never wording. One
 * owner for the operations applier (a Save's `@` checks, the removal
 * cascade) and the editor's live check (E-session-10).
 */

/** The fields of an asset a mention reads. */
export interface MentionableAsset {
  id: string;
  stableKey: string;
  type: unknown;
  name?: unknown;
  versionOf?: unknown;
}

function cleanText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * The assets the tokens' keys name in `assets`: each key's asset and the
 * other price versions of its product (`priceVersionsOf`, the typed link), in
 * the order mentions are matched: longest name first, then catalog order.
 */
function assetsOfTokens<T extends MentionableAsset>(
  keys: ReadonlyArray<string>,
  assets: ReadonlyArray<T>,
): Array<T> {
  if (keys.length === 0) return [];
  const named = new Set(
    keys.flatMap((key) => {
      const asset = assets.find((candidate) => candidate.stableKey === key);
      if (!asset) return [];
      const versions = priceVersionsOf(asset, assets);
      return versions.length > 0 ? versions : [asset];
    }),
  );
  return assets
    .map((asset, index) => ({ asset, index, name: cleanText(asset.name) }))
    .filter(({ asset }) => named.has(asset))
    .sort((a, b) => b.name.length - a.name.length || a.index - b.index)
    .map(({ asset }) => asset);
}

/**
 * Every asset a piece of authored text mentions: the assets its tokens name.
 * An explicit `@Name` the builder's mention field wrote, which the Save binds
 * to its token (`bindAssetMentionTokens`), is read through that one binder
 * first, so a check that runs before the Save binds the batch reads what the
 * Save will store, never a second reading of names (wave H M3: the alias
 * regex this held was a second parser, and it missed the binder's headline
 * and message aliases).
 */
export function findMentionedAssets<T extends MentionableAsset>(
  label: unknown,
  assets: ReadonlyArray<T>,
): Array<T> {
  const source = cleanText(label);
  if (!source) {
    return [];
  }
  const bound = source.includes("@")
    ? bindAssetMentionTokens(source, buildAssetMentionBindingTable({ assets: [...assets] }))
    : source;
  return assetsOfTokens(assetRefKeysIn(bound), assets);
}
